import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Footprints, MessageCircle, Pencil, Phone, StickyNote, Trash2, GitCommitHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api, formatErr } from "@/lib/api";
import { canDelete, FOLLOWUP_TYPES, STAGES } from "@/lib/constants";
import { areaText, budgetRange, fmtDateTime, fromISTInput } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useUserMap } from "@/hooks/useData";
import { Field, InfoRow, NSelect, Spinner, StageBadge, TempBadge, UserAvatar } from "@/components/common";
import { ContactButtons, FollowChip } from "@/components/leads/LeadCard";
import { useStageMove } from "@/components/leads/LeadKanban";
import { ConfirmDialog } from "@/components/Dialogs";
import DatePicker from "@/components/DatePicker";
import MatchList from "@/components/MatchList";
import { cn } from "@/lib/utils";

const TYPE_ICON = { Call: Phone, Visit: Footprints, WhatsApp: MessageCircle, Note: StickyNote, Stage: GitCommitHorizontal };

function FollowupForm({ leadId }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ type: "Call", outcome: "", notes: "", next: "" });
  const m = useMutation({
    mutationFn: () => api.post(`/leads/${leadId}/followups`, { type: f.type, outcome: f.outcome, notes: f.notes, next_followup_at: fromISTInput(f.next) }),
    onSuccess: () => {
      toast.success("Follow-up logged");
      setF({ type: "Call", outcome: "", notes: "", next: "" });
      qc.invalidateQueries({ queryKey: ["lead", leadId] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(formatErr(e)),
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }} className="space-y-3 rounded-2xl border bg-card p-5" data-testid="followup-form">
      <h3 className="font-display font-bold">Log follow-up</h3>
      <div className="flex gap-1.5">
        {FOLLOWUP_TYPES.map((t) => {
          const I = TYPE_ICON[t];
          return (
            <button type="button" key={t} data-testid={`followup-type-${t.toLowerCase()}`} onClick={() => setF({ ...f, type: t })}
              className={cn("flex flex-1 flex-col items-center gap-1 rounded-lg border py-2 text-[11px] font-semibold transition-colors", f.type === t ? "border-primary bg-primary/10 text-primary" : "hover:bg-secondary")}>
              <I className="h-4 w-4" />{t}
            </button>
          );
        })}
      </div>
      <Input data-testid="followup-outcome-input" value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} placeholder="Outcome (e.g. Interested, call back Friday)" required />
      <Textarea data-testid="followup-notes-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" rows={2} />
      <Field label="Next follow-up (IST)" hint="Leave empty to clear the next follow-up">
        <DatePicker withTime testid="followup-next-input" value={f.next} onChange={(v) => setF({ ...f, next: v })} />
      </Field>
      <Button type="submit" disabled={m.isPending} className="w-full" data-testid="followup-submit-button">
        {m.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save follow-up
      </Button>
    </form>
  );
}

function Timeline({ items }) {
  if (!items?.length) return <p className="py-6 text-center text-sm text-muted-foreground">No activity yet</p>;
  return (
    <ol className="relative space-y-5 border-l pl-6" data-testid="lead-timeline">
      {items.map((t) => {
        const I = TYPE_ICON[t.type] || StickyNote;
        return (
          <li key={t.id} className="relative" data-testid={`timeline-item-${t.id}`}>
            <span className="absolute -left-[37px] flex h-6 w-6 items-center justify-center rounded-full border bg-card"><I className="h-3 w-3 text-primary" /></span>
            <p className="text-sm"><span className="font-semibold">{t.type === "Stage" ? "Stage changed" : t.type}</span> · {t.outcome}</p>
            {t.notes && <p className="mt-0.5 text-sm text-muted-foreground">{t.notes}</p>}
            <p className="mt-1 text-xs text-muted-foreground">{t.user_name} · {fmtDateTime(t.at)}</p>
          </li>
        );
      })}
    </ol>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const userMap = useUserMap();
  const [del, setDel] = useState(false);
  const { move, dialog } = useStageMove();
  const { data: lead, isLoading, error } = useQuery({ queryKey: ["lead", id], queryFn: async () => (await api.get(`/leads/${id}`)).data });

  const remove = async () => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      qc.invalidateQueries({ queryKey: ["leads"] });
      nav("/leads");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (isLoading) return <Spinner />;
  if (error) return <p className="text-destructive">{formatErr(error)}</p>;
  const owner = userMap[lead.assigned_to];
  return (
    <div className="space-y-6">
      <Link to="/leads" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="lead-back-link"><ArrowLeft className="h-4 w-4" />Leads</Link>
      <div className="flex flex-col gap-4 rounded-2xl border bg-card p-5 sm:flex-row sm:items-start sm:justify-between fade-up">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight" data-testid="lead-detail-name">{lead.name}</h1>
            <TempBadge t={lead.temperature} />
            <StageBadge stage={lead.stage} />
          </div>
          <p className="num mt-1 text-sm text-muted-foreground" data-testid="lead-detail-phone">{lead.phone}{lead.alt_phone ? ` · ${lead.alt_phone}` : ""}{lead.email ? ` · ${lead.email}` : ""}</p>
          <div className="mt-2"><FollowChip at={lead.stage.startsWith("Closed") ? null : lead.next_followup_at} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ContactButtons lead={lead} size="lg" />
          <Button variant="outline" asChild data-testid="lead-edit-button"><Link to={`/leads/${id}/edit`}><Pencil className="mr-1.5 h-4 w-4" />Edit</Link></Button>
          {canDelete(user) && <Button variant="outline" className="text-destructive" onClick={() => setDel(true)} data-testid="lead-delete-button"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <section className="rounded-2xl border bg-card p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-display font-bold">Requirement</h3>
              <div className="w-56"><NSelect testid="lead-stage-select" value={lead.stage} onChange={(s) => move(lead, s)} options={STAGES} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <InfoRow label="Lead type" value={lead.lead_type} />
              <InfoRow label="Property type" value={lead.property_type} />
              <InfoRow label="BHK" value={lead.bhk} />
              <InfoRow label="Budget" value={budgetRange(lead.budget_min, lead.budget_max)} testid="lead-detail-budget" />
              <InfoRow label="Area" value={lead.area_min || lead.area_max ? `${areaText(lead.area_min, lead.area_unit)} – ${areaText(lead.area_max, lead.area_unit)}` : "—"} />
              <InfoRow label="Purpose" value={lead.purpose} />
              <InfoRow label="Locations" value={lead.locations?.join(", ")} />
              <InfoRow label="Source" value={lead.source} />
              <InfoRow label="Assigned to" value={owner ? <span className="inline-flex items-center gap-2"><UserAvatar user={owner} />{owner.name}</span> : "Unassigned"} />
              {lead.stage === "Closed Lost" && <InfoRow label="Lost reason" value={lead.lost_reason} />}
              <InfoRow label="Created" value={fmtDateTime(lead.created_at)} />
            </div>
            {lead.notes && <p className="mt-4 whitespace-pre-wrap rounded-lg bg-secondary/60 p-3 text-sm">{lead.notes}</p>}
          </section>
          <section className="rounded-2xl border bg-card p-5">
            <h3 className="mb-5 font-display font-bold">Follow-up timeline</h3>
            <Timeline items={lead.timeline} />
          </section>
          <section className="rounded-2xl border bg-card p-5" data-testid="lead-matches-section">
            <h3 className="mb-4 font-display font-bold">Matches</h3>
            <MatchList params={{ lead_id: id }} testid="lead-matches" />
          </section>
        </div>
        <div><FollowupForm leadId={id} /></div>
      </div>
      <ConfirmDialog open={del} onOpenChange={setDel} title="Delete this lead?" text="This removes the lead and its follow-up history. This cannot be undone." onConfirm={remove} testid="lead-delete-dialog" />
      {dialog}
    </div>
  );
}
